/**
 * text-image -- Sub-modo embebible dentro de stepper-flow.
 *
 * Renderiza markdown (texto explicativo) + imagen opcional (lateral o sobre el texto).
 * Disenado para pasos descriptivos en tutoriales / onboarding.
 *
 * stepData:
 *   - text         (string, requerido) - contenido en markdown / texto plano
 *   - image        (string, opcional)  - URL de imagen (relativa o absoluta)
 *   - alt          (string, opcional)  - alt de la imagen
 *   - imagePosition ('right'|'top'|'bottom', default 'right')
 *   - heading      (string, opcional)  - titulo del paso (h4)
 *
 * stepCtx (provisto por stepper-flow):
 *   - escHtml(s)               - escape seguro
 *   - markRead(stepIndex)      - marca paso como completado (auto al renderizar este sub-modo)
 *   - recordResult(stepIndex, data)  - guarda data en results
 *
 * NO emite mode-action; es paso "informativo" donde el usuario lee y avanza con boton next.
 *
 * @module presentation/components/ActionAreaModes/text-image
 */

import { marked } from 'marked';

export function renderTextImage(container, stepData, stepCtx) {
  const heading = stepData?.heading || '';
  const text = stepData?.text || '';
  const image = stepData?.image || '';
  const alt = stepData?.alt || 'imagen del paso';
  const imagePosition = stepData?.imagePosition || 'right';

  const textHtml = text ? marked.parse(text) : '<em style="color:#8b949e">Sin contenido</em>';
  const safeAlt = stepCtx.escHtml(alt);
  const safeImg = stepCtx.escHtml(image);

  const imageBlock = image
    ? `<div class="aa-step-image" style="flex-shrink:0;">
         <img src="${safeImg}" alt="${safeAlt}"
              style="max-width:100%;max-height:240px;object-fit:contain;border-radius:6px;border:1px solid #30363d;background:#0d1117;display:block"/>
       </div>`
    : '';

  let layout;
  if (image && imagePosition === 'right') {
    layout = `
      <div style="display:flex;gap:14px;align-items:flex-start;flex-wrap:wrap">
        <div style="flex:1;min-width:240px;color:#c9d1d9;line-height:1.55;font-size:0.9rem">
          ${heading ? `<h4 style="margin:0 0 8px;color:#f0f6fc;font-size:0.98rem">${stepCtx.escHtml(heading)}</h4>` : ''}
          ${textHtml}
        </div>
        <div style="flex:0 0 240px;max-width:240px">${imageBlock}</div>
      </div>
    `;
  } else if (image && imagePosition === 'top') {
    layout = `
      ${imageBlock}
      <div style="margin-top:12px;color:#c9d1d9;line-height:1.55;font-size:0.9rem">
        ${heading ? `<h4 style="margin:0 0 8px;color:#f0f6fc;font-size:0.98rem">${stepCtx.escHtml(heading)}</h4>` : ''}
        ${textHtml}
      </div>
    `;
  } else if (image && imagePosition === 'bottom') {
    layout = `
      <div style="color:#c9d1d9;line-height:1.55;font-size:0.9rem">
        ${heading ? `<h4 style="margin:0 0 8px;color:#f0f6fc;font-size:0.98rem">${stepCtx.escHtml(heading)}</h4>` : ''}
        ${textHtml}
      </div>
      <div style="margin-top:12px">${imageBlock}</div>
    `;
  } else {
    layout = `
      <div style="color:#c9d1d9;line-height:1.55;font-size:0.9rem">
        ${heading ? `<h4 style="margin:0 0 8px;color:#f0f6fc;font-size:0.98rem">${stepCtx.escHtml(heading)}</h4>` : ''}
        ${textHtml}
      </div>
    `;
  }

  container.innerHTML = `<div class="aa-step-text-image" data-aa-substep-type="text-image">${layout}</div>`;
  // Paso informativo: marcar como completado al renderizar (no hay accion explicita)
  stepCtx.recordResult({ acknowledged: true });
}
